import { useInfiniteQuery } from '@tanstack/react-query';
import { WorkplaceRequestTypesParams, WorkplaceRequestTypesResponse } from '@gettactic/api';
import { api } from '@/lib/api/api';
import { useAuthenticated } from '@gettactic/hooks';
import { canScheduleOthers } from '@/lib/permissions';

export function useWorkplaceRequestTypes(
  filters: WorkplaceRequestTypesParams = {},
  options: {
    enabled?: boolean;
    getNextPageParam?: (
      lastPage: WorkplaceRequestTypesResponse | null
    ) => string | null;
    keepPreviousData?: boolean;
  } = {}
) {
  const { userContext: { authenticatedUser } } = useAuthenticated();
  const isAdmin = canScheduleOthers(authenticatedUser);
  

  // Set default limit if not provided
  const defaultOptions = {
    enabled: true,
    getNextPageParam: (lastPage: WorkplaceRequestTypesResponse | null) => {
      if (!lastPage?.cursor) {
        return null;
      }
      return lastPage.cursor;
    },
    keepPreviousData: true
  };

  const {
    data,
    error,
    fetchNextPage,
    refetch,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status
  } = useInfiniteQuery(
    ['workplaceRequestTypes', filters, isAdmin],
    async ({ pageParam: cursor }) => {
      const queryFilters = { ...filters, isAdmin };
      if (cursor) {
        queryFilters.cursor = cursor;
      }
      const res = await api.client.workplaceRequestTypes.getWorkplaceRequestTypes(queryFilters);
      return res.result;
    },
    {
      ...defaultOptions,
      ...options,
      getNextPageParam: (lastPage) => {
        // If we don't have a cursor, we've reached the end
        if (!lastPage?.cursor) {
          return null;
        }

        // If we got fewer elements than the limit, we've reached the end
        if (lastPage.elements.length < (filters.limit ?? 100)) {
          return null;
        }

        return lastPage.cursor;
      }
    }
  );

  // Flatten all elements from all pages for easier consumption
  const allElements = data?.pages.flatMap((page) => page?.elements ?? []) ?? [];
  // Get the latest page data for other information
  const latestPage = data?.pages[data.pages.length - 1];

  return {
    data,
    error,
    fetchNextPage,
    refetch,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status,
    // Flattened data for easier consumption
    allElements,
    // Latest page data for totals
    totalElements: allElements.length
  };
}
